/**
 * Reordering a short list by hand.
 *
 * A grip on each row; press it and move, and the row travels with the pointer
 * while the others step aside. Letting go keeps the new order and tells the
 * caller, who saves it. Until the save answers, the list shows the order the
 * gardener chose rather than the one the server last confirmed, and if the
 * save fails the list falls back to what the server has.
 *
 * Pointer events rather than HTML drag-and-drop, because the latter does not
 * exist on a phone, and this is a garden app. The grip also takes the arrow
 * keys, so a keyboard can do the same job one step at a time.
 *
 * No library. The lists this serves are a dozen rows at most, and a dependency
 * that drags anything anywhere is more than that needs.
 *
 * Who uses it: the profile's StageSettings and NoteShortcutSettings, whose
 * saves are api/stages reorderStages and api/noteShortcuts
 * reorderNoteShortcuts.
 *
 * @module useDragReorder
 */

import React from 'react';
import { DRAG_REORDER_KEYBOARD_COMMIT_MS } from '@/config/tunables/shared';

/** What the hook is given. */
interface Options<T> {
  /** The list as the server has it, in order. */
  items: T[];
  /** A row's id; must be stable (memoised), or the list resets every render. */
  idOf: (item: T) => number;
  /** Saves a new order. The ids are in the order they should be kept. */
  onCommit: (ids: number[]) => Promise<void>;
  /** True while the list must not be touched, e.g. during another save. */
  disabled?: boolean;
  /** Which way the rows run: down a list, or along a strip. */
  axis?: 'y' | 'x';
}

/** What the hook hands back: the list to draw, and props for each row and its grip. */
export interface DragReorder<T> {
  /** The list in the order to draw it. */
  ordered: T[];
  /** The id of the row being moved, or null. */
  dragging: number | null;
  /** Spread on each row's element. */
  rowProps: (id: number) => {
    ref: (el: HTMLElement | null) => void;
    'data-dragging': '' | undefined;
  };
  /** Spread on each row's grip. */
  handleProps: (id: number) => {
    onPointerDown: (event: React.PointerEvent) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    'aria-grabbed': boolean;
    style: React.CSSProperties;
  };
}

/** How long after the last arrow key the new order is saved. */
const KEYBOARD_COMMIT_DELAY_MS = DRAG_REORDER_KEYBOARD_COMMIT_MS;

/**
 * Lets a short list be reordered by dragging a grip, or by its arrow keys.
 *
 * @param options - The list, its ids, the save, and which way it runs.
 * @returns The list in the order to draw it, and props for rows and grips.
 */
export function useDragReorder<T>({ items, idOf, onCommit, disabled = false, axis = 'y' }: Options<T>): DragReorder<T> {
  const serverOrder = React.useMemo(() => items.map(idOf), [items, idOf]);
  /** The order in hand, when it differs from the server's; null when it does not. */
  const [order, setOrder] = React.useState<number[] | null>(null);
  const [dragging, setDragging] = React.useState<number | null>(null);
  const rows = React.useRef(new Map<number, HTMLElement>());
  const keyboardTimer = React.useRef<number | undefined>(undefined);

  // A fresh list from the server wins: it is what the last save produced.
  React.useEffect(() => { setOrder(null); }, [serverOrder]);

  const current = order ?? serverOrder;
  const byId = React.useMemo(() => new Map(items.map((item) => [idOf(item), item])), [items, idOf]);
  const ordered = React.useMemo(
    () => current.map((id) => byId.get(id)).filter((item): item is T => item !== undefined),
    [current, byId]
  );

  /** Keeps a new order in hand while it saves; the server's order again if the save fails. */
  const commit = React.useCallback(async (ids: number[]) => {
    if (ids.join(',') === serverOrder.join(',')) { setOrder(null); return; }
    setOrder(ids);
    try {
      await onCommit(ids);
    } catch {
      // The server's order stands; the caller has reported the failure. Set
      // explicitly rather than waiting for a refetch, because a refetch that
      // returns the same rows keeps the same array and would not wake the
      // reset effect below.
      setOrder(null);
    }
  }, [onCommit, serverOrder]);

  /**
   * Where a pointer at this height (or, along a strip, this x) belongs in the list.
   *
   * @param at - The pointer's position on the list's axis, in client pixels.
   * @param ids - The order being dragged.
   * @returns The index it would go to.
   */
  const indexAt = React.useCallback((at: number, ids: number[]): number => {
    for (let i = 0; i < ids.length; i += 1) {
      const el = rows.current.get(ids[i]);
      if (!el) continue;
      const box = el.getBoundingClientRect();
      const mid = axis === 'x' ? box.left + box.width / 2 : box.top + box.height / 2;
      if (at < mid) return i;
    }
    return ids.length - 1;
  }, [axis]);

  /** Starts a drag from a row's grip: the row follows the pointer until it is let go. */
  const onPointerDown = React.useCallback((id: number) => (event: React.PointerEvent) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    const start = order ?? serverOrder;
    let working = start.slice();
    setDragging(id);

    /** Moves the row to where the pointer now is. */
    const move = (e: PointerEvent) => {
      const from = working.indexOf(id);
      const to = indexAt(axis === 'x' ? e.clientX : e.clientY, working);
      if (from === -1 || to === from) return;
      const next = working.slice();
      next.splice(from, 1);
      next.splice(to, 0, id);
      working = next;
      setOrder(next);
    };
    /** Lets go: stops following and saves the order. */
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(null);
      void commit(working);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }, [disabled, order, serverOrder, indexAt, commit, axis]);

  /** Moves a row one step with the arrow keys, saving once the presses stop. */
  const onKeyDown = React.useCallback((id: number) => (event: React.KeyboardEvent) => {
    if (disabled) return;
    const back = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
    const forward = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
    const delta = event.key === back ? -1 : event.key === forward ? 1 : 0;
    if (!delta) return;
    event.preventDefault();
    const start = order ?? serverOrder;
    const from = start.indexOf(id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= start.length) return;
    const next = start.slice();
    next.splice(from, 1);
    next.splice(to, 0, id);
    setOrder(next);
    // Several presses in a row are one decision, saved once they stop.
    window.clearTimeout(keyboardTimer.current);
    keyboardTimer.current = window.setTimeout(() => { void commit(next); }, KEYBOARD_COMMIT_DELAY_MS);
  }, [disabled, order, serverOrder, commit, axis]);

  React.useEffect(() => () => window.clearTimeout(keyboardTimer.current), []);

  /** Props for a row: its element is remembered for hit-testing, and it is marked while dragged. */
  const rowProps = React.useCallback((id: number) => ({
    ref: (el: HTMLElement | null) => { if (el) rows.current.set(id, el); else rows.current.delete(id); },
    'data-dragging': (dragging === id ? '' : undefined) as '' | undefined,
  }), [dragging]);

  /** Props for a row's grip: pointer, keys and the state screen readers hear. */
  const handleProps = React.useCallback((id: number) => ({
    onPointerDown: onPointerDown(id),
    onKeyDown: onKeyDown(id),
    'aria-grabbed': dragging === id,
    // Without this a finger on the grip scrolls the page instead of the row.
    style: { touchAction: 'none' } as React.CSSProperties,
  }), [onPointerDown, onKeyDown, dragging]);

  return { ordered, dragging, rowProps, handleProps };
}
